/**
 * «Передать соседу» (план 21.09, уровень 0): кнопка в аварийном режиме и модал
 * с QR — рядом стоящий человек сканирует ЭКРАН и получает тот же режим, даже
 * если сети у него нет (кадр целиком в ссылке, см. emergency/pass-link.ts).
 * Анти-паника: спокойная подача, ничего не мигает; QR рисует тот же пакет
 * qrcode, что и обычный QR-пропуск (ui/qr-pass.tsx).
 *
 * Кнопка и модал живут одним компонентом: в аварийном режиме на экране всегда
 * ровно один из них — герой ИЛИ карта, поэтому двух модалов не бывает.
 */
import { tr } from '@app/i18n'
import { signal } from '@preact/signals'
import { useEffect, useState } from 'preact/hooks'
import { buildPassLink, passDrillHazard } from '../emergency/pass-link'
import { importChunk } from '../lazy-retry'
import { emergencyDrill, emergencyFrame, emergencyHazard, langTick } from '../state'

/** Модал открыт. Локальный сигнал: состояние не переживает выход из ЧС. */
const passOpen = signal(false)

/** Ссылка для соседа по текущему кадру (боевому или учебному). */
function currentLink(): string {
  const frame = emergencyFrame.value
  if (!frame) return ''
  return buildPassLink(
    location.origin,
    frame,
    passDrillHazard(emergencyDrill.value, emergencyHazard.value),
  )
}

/** Кнопка + модал. `variant="map"` — компактная кнопка в колонке инструментов. */
export function EmergencyPass({ variant }: { variant?: 'map' }) {
  langTick.value
  if (!emergencyFrame.value) return null
  return (
    <>
      <button
        type="button"
        class={variant === 'map' ? 'em-pass-btn em-pass-btn--map' : 'em-pass-btn'}
        aria-label={tr('emergency.pass_btn')}
        title={tr('emergency.pass_btn')}
        onClick={() => {
          passOpen.value = true
        }}
      >
        {variant === 'map' ? '⇄' : tr('emergency.pass_btn')}
      </button>
      {passOpen.value && <EmergencyPassModal />}
    </>
  )
}

function EmergencyPassModal() {
  langTick.value
  const link = currentLink()
  const [dataUrl, setDataUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    setDataUrl(null)
    setCopied(false)
    if (!link) return
    void importChunk(() => import('qrcode')).then(async (qrcode) => {
      setDataUrl(await qrcode.toDataURL(link, { width: 480, margin: 1 }))
    })
  }, [link])

  // Esc закрывает модал (клавиатурный аналог клика по фону) — как в qr-pass.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') passOpen.value = false
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  if (!link) return null

  const close = (): void => {
    passOpen.value = false
  }
  const copy = (): void => {
    void navigator.clipboard?.writeText(link).then(() => setCopied(true))
  }
  const share = (): void => {
    if (navigator.share) {
      void navigator.share({ title: tr('emergency.pass_title'), url: link }).catch(() => undefined)
    } else {
      copy()
    }
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: клик по фону — вторичный жест закрытия; клавиатура — Esc и кнопка
    <div class="qrpass-backdrop em-pass-backdrop" onClick={close}>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation, не действие */}
      <div class="qrpass glass em-pass" onClick={(e) => e.stopPropagation()}>
        <div class="qrpass-head">
          <h2 class="qrpass-title">{tr('emergency.pass_title')}</h2>
          <button
            type="button"
            class="icon-btn"
            aria-label={tr('emergency.pass_close')}
            onClick={close}
          >
            ✕
          </button>
        </div>
        <div class="qrpass-card">
          {dataUrl ? (
            <img class="qrpass-img" src={dataUrl} alt={tr('qrpass.alt')} />
          ) : (
            <div class="qrpass-img qrpass-loading" aria-busy="true" />
          )}
          <p class="tech-label qrpass-scan">{tr('emergency.pass_hint')}</p>
        </div>
        <div class="qrpass-actions">
          <button type="button" class="measure-btn" onClick={copy}>
            {copied ? tr('qrpass.copied') : tr('qrpass.copy')}
          </button>
          <button type="button" class="measure-btn measure-btn-done" onClick={share}>
            {tr('qrpass.share')}
          </button>
        </div>
      </div>
    </div>
  )
}
